import React, { useMemo, useState } from 'react';
import { Navigate, useOutletContext, useSearchParams } from 'react-router';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AddIcon from '@mui/icons-material/Add';
import { SEOHelmet } from 'components/SEO';
import { PlayPrimaryButton } from '../../components/PlayPrimaryButton';
import {
  useFinishTournamentMutation,
  usePairNextRoundMutation,
  useRepairRoundMutation,
  useResolveJudgeCallMutation,
  useRoundControlMutation,
  useSwapPlayersMutation,
} from 'store/services/play/playApi';
import type { MatchState, PlayPlayer } from 'types/interfaces/play';
import { playerName } from 'types/interfaces/play';
import { playErrorMessage } from 'utils/play/playErrors';
import { playPath } from 'utils/play/inviteUrl';
import PlayNotFound from '../../PlayNotFound';
import { PlayLayout } from '../../layout/PlayLayout';
import { hostTabs, type HostTabKey } from '../../layout/playTabs';
import type { TournamentOutletContext } from '../../PlayRoutes';
import { useTournament } from '../../hooks/useTournament';
import { useRoundClock } from '../../hooks/useRoundClock';
import { StandingsTable } from '../../components/StandingsTable';
import { isEliminationRound } from '../../matchView';
import { formatClock, tournamentStatusLabel } from '../../playFormat';
import { TournamentLoading } from '../Tournament/TournamentHomePage';
import { standingsSubtitle } from '../Tournament/StandingsPage';
import AddPlayerSheet from './AddPlayerSheet';
import { MatchSheet, PlayerSheet } from './hostSheets';
import './HostLobby.css';
import './HostRound.css';

type Filter = 'all' | 'pending' | 'reported' | 'issues';
const TABS: HostTabKey[] = ['round', 'players', 'standings'];

/** Decision 21 chips: Pending, Reported, Disputed, Judge, Bye. */
const chipFor = (m: MatchState): { label: string; cls: string } => {
  if (m.openJudgeCall) return { label: 'Judge', cls: 'play-status-chip--judge' };
  if (m.status === 'DISPUTED') return { label: 'Disputed', cls: 'play-status-chip--disputed' };
  if (m.isBye) return { label: 'Bye', cls: '' };
  if (m.status === 'REPORTED') return { label: 'Reported', cls: 'play-status-chip--reported' };
  return { label: 'Pending', cls: '' };
};

/**
 * The organizer console (Decisions 11, 12, 13, 15, 16, 21, 23): Round, Players
 * and Standings tabs on one route with ?tab=, the Round badge counting open
 * judge calls plus disputes.
 */
const HostConsolePage: React.FC = () => {
  const { code } = useOutletContext<TournamentOutletContext>();
  const [params, setParams] = useSearchParams();
  const { state, isLoading, notFound } = useTournament(code);
  const tabParam = params.get('tab');
  const tab: HostTabKey = TABS.includes(tabParam as HostTabKey) ? (tabParam as HostTabKey) : 'round';
  const initialFilter = params.get('filter');
  const [filter, setFilter] = useState<Filter>(initialFilter === 'issues' ? 'issues' : 'all');
  const [editPairs, setEditPairs] = useState(false);
  const [swapSel, setSwapSel] = useState<string | null>(null);
  const [sheet, setSheet] = useState<{ kind: 'match'; match: MatchState } | { kind: 'player'; player: PlayPlayer } | { kind: 'add' } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [pairNext, { isLoading: pairing }] = usePairNextRoundMutation();
  const [repair, { isLoading: repairing }] = useRepairRoundMutation();
  const [swap, { isLoading: swapping }] = useSwapPlayersMutation();
  const [control, { isLoading: controlling }] = useRoundControlMutation();
  const [resolveJudge, { isLoading: resolving }] = useResolveJudgeCallMutation();
  const [finish, { isLoading: finishing }] = useFinishTournamentMutation();

  const round = state?.currentRound ?? null;
  const clock = useRoundClock(round);

  const rows = useMemo(() => {
    const matches = round?.matches ?? [];
    switch (filter) {
      case 'pending':
        return matches.filter((m) => m.status === 'PENDING' && !m.isBye);
      case 'reported':
        return matches.filter((m) => m.status === 'REPORTED');
      case 'issues':
        return matches.filter((m) => m.status === 'DISPUTED' || m.openJudgeCall);
      default:
        return matches;
    }
  }, [round, filter]);

  if (notFound) return <PlayNotFound reason="missing" />;
  if (isLoading || !state) return <TournamentLoading />;
  if (state.viewer.role !== 'TO') return <Navigate replace to={playPath(code)} />;
  const { tournament } = state;
  if (tournament.status === 'LOBBY') return <Navigate replace to={playPath(code)} />;

  const judgeCalls = state.judgeCalls ?? [];
  const disputes = round?.matches.filter((m) => m.status === 'DISPUTED').length ?? 0;
  const badge = judgeCalls.length + disputes;
  const live = tournament.status === 'LIVE' && !!round?.startedAt;
  const pairedOnly = !!round && !round.startedAt && tournament.status !== 'COMPLETE';
  const complete = tournament.status === 'COMPLETE';
  const elimination = isEliminationRound(state, round);
  const resultsIn = round?.resultsIn ?? 0;
  const resultsTotal = round?.resultsTotal ?? 0;
  const allIn = !!round && resultsIn >= resultsTotal;
  const pendingCount = Math.max(0, resultsTotal - resultsIn);
  const isLastRound = !!round && round.number >= tournament.totalRounds;
  const isLastSwiss = !!round && round.phase === 'SWISS' && round.number >= tournament.swissRounds;
  const startsTopCut = tournament.structure === 'SWISS_TOP_CUT' && isLastSwiss;
  const nextLabel = !allIn
    ? `Waiting on ${pendingCount} result${pendingCount === 1 ? '' : 's'}`
    : startsTopCut
      ? 'Start top cut'
      : isLastRound
        ? round?.phase === 'SWISS'
          ? 'Finish Swiss & post standings'
          : 'Finish & post standings'
        : `Pair round ${(round?.number ?? 0) + 1}`;

  const setTab = (next: HostTabKey) => {
    const p = new URLSearchParams(params);
    p.set('tab', next);
    p.delete('filter');
    setParams(p, { replace: true });
  };

  const run = async (fn: () => Promise<unknown>, fallback: string) => {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(playErrorMessage(err, fallback));
    }
  };

  const onNext = () =>
    run(async () => {
      if (!round) return;
      if (allIn && isLastRound && !startsTopCut) {
        await finish({ id: tournament.id, code }).unwrap();
      } else {
        await pairNext({ id: tournament.id, code }).unwrap();
        setFilter('all');
      }
    }, "Couldn't advance the round.");

  const onSeatTap = (m: MatchState, side: 'A' | 'B') => {
    const id = side === 'A' ? m.playerA.tournamentPlayerId : m.playerB?.tournamentPlayerId;
    if (!id || !round) return;
    if (!swapSel) {
      setSwapSel(id);
      return;
    }
    if (swapSel === id) {
      setSwapSel(null);
      return;
    }
    const a = swapSel;
    setSwapSel(null);
    void run(() => swap({ roundId: round.id, code, playerIdA: a, playerIdB: id }).unwrap(), "Couldn't swap those players.");
  };

  const tabs = hostTabs(code, tab, badge);
  const subtitle = `${tournamentStatusLabel(tournament.status, round?.number ?? null, tournament.totalRounds)} · ${tournament.activeCount} players`;

  return (
    <PlayLayout title={tournament.name} subtitle={subtitle} tabs={tabs.map((t) => ({ ...t, to: t.to }))} backTo="/play">
      <SEOHelmet title={`${tournament.name} - Organizer - TradeMagic Play`} description="Round control." noindex />

      {tab === 'round' && (
        <>
          {complete && (
            <section className="play-card play-winner" data-testid="host-complete">
              <p className="play-eyebrow">Tournament complete</p>
              {state.standings[0] && (
                <>
                  <p className="play-winner__name">{state.standings[0].username}</p>
                  <p className="play-helper">
                    {state.standings[0].wins}-{state.standings[0].losses}-{state.standings[0].draws} · {state.standings[0].points} pts
                  </p>
                </>
              )}
              <p style={{ marginTop: 12 }}>
                <button type="button" className="play-secondary-button" onClick={() => setTab('standings')}>
                  Final standings
                </button>
              </p>
            </section>
          )}

          {!complete && round && (
            <section className="play-card play-clock-card" data-testid="host-clock-card">
              <div className="play-clock-card__top">
                <div>
                  <p className="play-eyebrow" style={{ margin: 0 }}>
                    Round {round.number} of {round.totalRounds}
                    {round.phase === 'TOP_CUT' ? ' · Top cut' : ''}
                  </p>
                  <div className={`play-clock play-clock--${live ? clock.tone : 'normal'}`} style={{ marginTop: 6 }}>
                    <span className="play-clock__time" data-testid="host-clock">
                      {clock.text}
                    </span>
                    <span className="play-clock__caption">{!live ? 'Not started' : clock.tone === 'overtime' ? 'Time called' : clock.isPaused ? 'Paused' : 'Time remaining'}</span>
                  </div>
                </div>
                {live && (
                  <div className="play-clock-card__controls">
                    <button
                      type="button"
                      className="play-icon-button"
                      onClick={() => run(() => control({ roundId: round.id, code, action: clock.isPaused ? 'resume' : 'pause' }).unwrap(), "Couldn't change the clock.")}
                      disabled={controlling}
                      aria-label={clock.isPaused ? 'Resume clock' : 'Pause clock'}
                      data-testid="host-pause"
                    >
                      {clock.isPaused ? <PlayArrowIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
                      {clock.isPaused ? 'Resume' : 'Pause'}
                    </button>
                    <button
                      type="button"
                      className="play-icon-button"
                      onClick={() => run(() => control({ roundId: round.id, code, action: 'extend' }).unwrap(), "Couldn't extend the round.")}
                      disabled={controlling}
                      data-testid="host-extend"
                    >
                      <AddIcon fontSize="small" />5
                    </button>
                  </div>
                )}
              </div>
              {live && (
                <div className="play-progress">
                  <div className="play-progress__bar">
                    <div className="play-progress__fill" style={{ width: `${resultsTotal ? Math.round((resultsIn / resultsTotal) * 100) : 0}%` }} />
                  </div>
                  <p className="play-progress__text" data-testid="host-progress">
                    {resultsIn} of {resultsTotal} results in
                  </p>
                </div>
              )}
              {pairedOnly && (
                <div className="play-round-actions">
                  <PlayPrimaryButton
                    label={`Start round ${round.number} · ${formatClock(round.durationSeconds)}`}
                    loading={controlling}
                    onClick={() => run(() => control({ roundId: round.id, code, action: 'start' }).unwrap(), "Couldn't start the round.")}
                  />
                  <p className="play-round-helper">Starts the clock and alerts every player with their table.</p>
                  <div className="play-sheet-drops">
                    <button type="button" className="play-secondary-button" onClick={() => run(() => repair({ roundId: round.id, code }).unwrap(), "Couldn't re-pair.")} disabled={repairing}>
                      Re-pair
                    </button>
                    <button
                      type="button"
                      className="play-secondary-button"
                      style={editPairs ? { background: 'rgba(111, 81, 228, 0.16)', outline: '1px solid rgba(111, 81, 228, 0.6)' } : undefined}
                      onClick={() => {
                        setEditPairs((v) => !v);
                        setSwapSel(null);
                      }}
                      data-testid="host-swap-toggle"
                    >
                      {editPairs ? 'Done editing' : 'Swap players'}
                    </button>
                  </div>
                  <p className="play-pair-hint">
                    {editPairs ? (swapSel ? 'Now tap the player to swap with' : 'Tap a player, then another to swap') : `${round.matches.length} tables · tap Swap to fix a pairing`}
                  </p>
                </div>
              )}
            </section>
          )}

          {live &&
            judgeCalls.map((j) => (
              <div key={j.id} className="play-judge-row" data-testid="host-judge-row">
                <div className="play-judge-row__main">
                  <span className="play-judge-row__title">Judge call · Table {j.tableNumber ?? '–'}</span>
                  <span className="play-judge-row__sub">Called by {j.calledByUsername}</span>
                </div>
                <button type="button" className="play-icon-button" onClick={() => run(() => resolveJudge({ judgeCallId: j.id, code }).unwrap(), "Couldn't resolve the call.")} disabled={resolving}>
                  Resolve
                </button>
              </div>
            ))}

          {live && (
            <div className="play-chips" role="tablist" aria-label="Match filter">
              {(
                [
                  ['all', 'All', round?.matches.length ?? 0],
                  ['pending', 'Pending', round?.matches.filter((m) => m.status === 'PENDING' && !m.isBye).length ?? 0],
                  ['reported', 'Reported', round?.matches.filter((m) => m.status === 'REPORTED').length ?? 0],
                  ['issues', 'Issues', badge],
                ] as const
              ).map(([key, label, count]) => (
                <button key={key} type="button" role="tab" aria-selected={filter === key} className={`play-chip ${filter === key ? 'play-chip--active' : ''}`} onClick={() => setFilter(key)} data-testid={`host-filter-${key}`}>
                  {label}
                  <span className="play-chip__count">{count}</span>
                </button>
              ))}
            </div>
          )}

          {!complete && round && (
            <ul className="play-match-list" data-testid="host-match-list">
              {rows.map((m) => {
                const chip = chipFor(m);
                const edit = editPairs && pairedOnly;
                const aWin = m.gamesA !== null && m.gamesB !== null && m.gamesA > m.gamesB;
                const bWin = m.gamesA !== null && m.gamesB !== null && m.gamesB > m.gamesA;
                const tappable = live && !m.isBye;
                const Row: React.ElementType = tappable ? 'button' : 'div';
                return (
                  <li key={m.id}>
                    <Row
                      className={`play-match-row ${tappable ? 'play-match-row--button' : ''} ${edit ? 'play-match-row--edit' : ''} ${m.openJudgeCall ? 'play-match-row--judge' : m.status === 'DISPUTED' ? 'play-match-row--disputed' : ''}`}
                      type={tappable ? 'button' : undefined}
                      onClick={tappable ? () => setSheet({ kind: 'match', match: m }) : undefined}
                      data-testid="host-match-row"
                    >
                      <span className="play-match-row__table">{m.isBye ? '—' : m.tableNumber}</span>
                      <span className="play-match-row__players">
                        <Seat name={m.playerA.username} score={m.gamesA} tone={aWin ? 'winner' : bWin ? 'loser' : undefined} selectable={edit} selected={swapSel === m.playerA.tournamentPlayerId} onTap={edit ? () => onSeatTap(m, 'A') : undefined} />
                        <Seat name={m.playerB?.username ?? 'Bye'} score={m.gamesB} tone={bWin ? 'winner' : aWin ? 'loser' : undefined} selectable={edit && !!m.playerB} selected={!!m.playerB && swapSel === m.playerB.tournamentPlayerId} onTap={edit && m.playerB ? () => onSeatTap(m, 'B') : undefined} />
                      </span>
                      <span className={`play-status-chip ${chip.cls}`}>{chip.label}</span>
                    </Row>
                  </li>
                );
              })}
              {rows.length === 0 && (
                <li>
                  <p className="play-helper">Nothing here.</p>
                </li>
              )}
            </ul>
          )}

          {live && (
            <div className="play-round-actions">
              <PlayPrimaryButton label={nextLabel} disabled={!allIn || pairing || finishing} loading={pairing || finishing} onClick={onNext} />
            </div>
          )}
          {swapping && <p className="play-round-helper">Swapping…</p>}
        </>
      )}

      {tab === 'players' && (
        <section className="play-card" data-testid="host-players">
          <div className="play-lobby__status">
            <p className="play-eyebrow" style={{ margin: 0 }}>
              Players
            </p>
            <span className="play-helper" style={{ fontSize: 12 }}>
              {tournament.activeCount} active · {tournament.registrationOpen ? 'late registration open' : 'registration closed'}
            </span>
          </div>
          <ul className="play-player-list">
            {state.players.map((p) => (
              <li key={p.id}>
                <button type="button" className={`play-player-row play-player-row--button ${p.dropped ? 'play-player-row--dropped' : ''}`} onClick={() => setSheet({ kind: 'player', player: p })} data-testid="host-player-row">
                  <span className="play-avatar-initial">{playerName(p).slice(0, 1).toUpperCase()}</span>
                  <span className="play-player-row__main">
                    <span className="play-player-row__name">{playerName(p)}</span>
                    <span className={`play-player-row__sub ${p.dropped ? '' : p.hasDecklist ? '' : 'play-player-row__sub--warn'}`}>
                      {[p.firstName, p.lastName].filter(Boolean).join(' ')}
                      {p.firstName || p.lastName ? ' · ' : ''}
                      {p.dropped ? 'Dropped' : p.hasDecklist ? p.deckName || 'Decklist in' : tournament.decklistRequired ? 'Decklist missing' : 'Registered'}
                    </span>
                  </span>
                  <span className="play-player-row__right">{p.record}</span>
                </button>
              </li>
            ))}
          </ul>
          <div className="play-lobby__actions">
            <button type="button" className="play-secondary-button" onClick={() => setSheet({ kind: 'add' })} disabled={!tournament.registrationOpen}>
              Add player
            </button>
          </div>
        </section>
      )}

      {tab === 'standings' && <StandingsTable rows={state.standings} variant="host" subtitle={standingsSubtitle(state.rounds, !!round && !allIn)} />}

      {error && (
        <p className="play-error" role="alert">
          {error}
        </p>
      )}

      {sheet?.kind === 'match' && <MatchSheet code={code} state={state} match={round?.matches.find((m) => m.id === sheet.match.id) ?? sheet.match} elimination={elimination} onClose={() => setSheet(null)} />}
      {sheet?.kind === 'player' && <PlayerSheet code={code} state={state} player={state.players.find((p) => p.id === sheet.player.id) ?? sheet.player} onClose={() => setSheet(null)} />}
      {sheet?.kind === 'add' && <AddPlayerSheet code={code} state={state} onClose={() => setSheet(null)} />}
    </PlayLayout>
  );
};

const Seat: React.FC<{ name: string; score: number | null; tone?: 'winner' | 'loser'; selectable: boolean; selected: boolean; onTap?: () => void }> = ({ name, score, tone, selectable, selected, onTap }) => {
  const cls = `play-seat ${selectable ? 'play-seat--selectable' : ''} ${selected ? 'play-seat--selected' : ''}`;
  const inner = (
    <>
      <span className={`play-seat__name ${tone ? `play-seat__name--${tone}` : ''}`}>{name}</span>
      {score !== null && <span className="play-seat__score">{score}</span>}
    </>
  );
  if (selectable && onTap) {
    return (
      <button
        type="button"
        className={cls}
        onClick={(e) => {
          e.stopPropagation();
          onTap();
        }}
        aria-pressed={selected}
      >
        {inner}
      </button>
    );
  }
  return <span className={cls}>{inner}</span>;
};

export default HostConsolePage;
